
import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CartItem } from "@/types/apparelTypes";
import { CartEmpty } from "./cart/CartEmpty";
import { CartItemComponent } from "./cart/CartItem";
import { CartFooter } from "./cart/CartFooter";

interface CartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CartItem[];
  cartItems: CartItem[];
  onRemoveItem: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSize: (index: number, size: string) => void;
  onCheckout: () => void;
  cartSubtotal: () => number;
}

const CartSheet: React.FC<CartSheetProps> = ({
  open,
  onOpenChange,
  items,
  cartItems,
  onRemoveItem,
  onUpdateQuantity,
  onUpdateSize,
  onCheckout,
  cartSubtotal,
}) => {
  const cartData = items || cartItems;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md flex flex-col bg-white border-l shadow-xl h-full max-h-screen">
        <SheetHeader className="text-left border-b pb-4 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <ShoppingBag className="h-5 w-5" />
            Your Cart
          </SheetTitle>
          <SheetDescription>
            {cartData.length === 0 ? 
              "Your cart is empty" : 
              `${cartData.length} item${cartData.length > 1 ? "s" : ""} in your cart`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex-1 mt-6 overflow-y-auto overscroll-contain">
          {cartData.length === 0 ? (
            <CartEmpty onClose={() => onOpenChange(false)} />
          ) : (
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6 pb-4"
            >
              <AnimatePresence>
                {cartData.map((item, index) => (
                  <CartItemComponent
                    key={`${item.product.id}-${index}`}
                    item={item}
                    index={index}
                    onRemove={onRemoveItem}
                    onUpdateQuantity={onUpdateQuantity}
                    onUpdateSize={onUpdateSize}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
        
        {/* Always visible footer - FIXED FOR MOBILE */}
        {cartData.length > 0 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white p-4 mt-auto">
            <CartFooter
              subtotal={cartSubtotal()}
              onCheckout={onCheckout}
              onClose={() => onOpenChange(false)}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
